<template>
  <!-- Words blocks (WEB_V2_BUILD.md §3) as the site shows them: the Words
       section, posts and projects all draw through here. Every block carries
       its own alignment; photos their position, size and caption; HTML is
       sanitised with the same rules as the old Words text. -->
  <div class="s2-blocks t-body">
    <template v-for="b in list" :key="b.id">
      <div v-if="b.kind === 'text'" class="s2-b s2-b--text s2-prose mo-reveal" :class="`s2-al-${alignOf(b.align)}`" :data-lead="b.lead ? 'true' : null" v-html="safeBlockHtml(b)" />

      <component v-else-if="b.kind === 'heading'" :is="Number(b.level) === 3 ? 'h3' : 'h2'" :id="headingIds[b.id]"
                 class="s2-b s2-b--heading mo-reveal" :class="[Number(b.level) === 3 ? 't-h3' : 't-h2', `s2-al-${alignOf(b.align)}`]">{{ plainText(b.text) }}</component>

      <div v-else-if="b.kind === 'photo'" class="s2-b s2-b--photo mo-reveal" :class="`s2-size-${b.size || 'l'}`">
        <Media :media="b.image" :caption="true" :zoom="b.zoom !== false" @zoom="zoomTo(b.image)" />
      </div>

      <div v-else-if="b.kind === 'photos' || b.kind === 'gallery'" class="s2-b s2-b--photos mo-stagger" :data-count="Math.min(mediaList(b.images).length, 3)">
        <Media v-for="(img, i) in (b.images || [])" :key="i" :media="img" :caption="b.captions !== false" :zoom="true" ratio="4 / 3" @zoom="zoomTo(img, b.images)" />
      </div>

      <div v-else-if="b.kind === 'photo-text'" class="s2-b s2-b--pt mo-reveal" :class="[`s2-pt-${b.position || 'left'}`, `s2-size-${b.size || 'm'}`]">
        <Media :media="b.image" class="s2-pt__media" :caption="true" :zoom="b.zoom !== false" @zoom="zoomTo(b.image)" />
        <div class="s2-prose" v-html="safeBlockHtml({html: b.html})" />
      </div>

      <blockquote v-else-if="b.kind === 'quote'" class="s2-b s2-b--quote mo-reveal" :class="`s2-al-${alignOf(b.align)}`">
        <p class="t-accent-text">{{ plainText(b.text) }}</p>
        <footer v-if="b.who" class="t-muted">{{ b.who }}</footer>
      </blockquote>

      <div v-else-if="b.kind === 'columns'" class="s2-b s2-b--cols mo-stagger" :style="{'--cols': Math.min(Math.max((b.cols || []).length, 1), 3)}">
        <div v-for="(c, i) in (b.cols || [])" :key="i" class="s2-prose" v-html="safeBlockHtml({html: c?.html})" />
      </div>

      <ul v-else-if="b.kind === 'ticks'" class="s2-b s2-b--ticks mo-stagger">
        <li v-for="(t, i) in (b.items || []).filter(Boolean)" :key="i"><span aria-hidden="true" class="s2-tick">✓</span>{{ typeof t === 'string' ? t : t.text }}</li>
      </ul>

      <aside v-else-if="b.kind === 'note'" class="s2-b s2-b--note t-card mo-reveal" v-html="safeBlockHtml({html: b.html})" />

      <div v-else-if="b.kind === 'button' && safeLink(b.link)" class="s2-b s2-b--button mo-reveal" :class="`s2-al-${alignOf(b.align)}`">
        <a class="t-btn" :href="back(safeLink(b.link))" :target="isExternal(b.link) ? '_blank' : null" :rel="isExternal(b.link) ? 'noopener' : null">{{ b.label || 'Find out more' }}</a>
      </div>

      <hr v-else-if="b.kind === 'divider'" class="s2-b s2-b--divider" />

      <div v-else-if="b.kind === 'video'" class="s2-b s2-b--video mo-reveal">
        <Media :media="{type: 'video', video: videoOf(b)}" :caption="true" />
      </div>

      <nav v-else-if="b.kind === 'links' && linksOf(b).length" class="s2-b s2-b--links mo-reveal" :aria-label="b.title || 'Links'">
        <h3 v-if="b.title" class="t-h3">{{ b.title }}</h3>
        <p v-if="b.subText" class="t-muted">{{ b.subText }}</p>
        <ul>
          <li v-for="(l, i) in linksOf(b)" :key="i"><a class="t-link s2-arrow" :href="back(l.href)">{{ l.label }}</a></li>
        </ul>
      </nav>

      <div v-else-if="b.kind === 'before-after'" class="s2-b s2-b--ba mo-stagger" :data-style="baStyle(b)">
        <figure v-for="(pair, i) in baPairs(b)" :key="i" class="s2-ba">
          <BeforeAfterSlider v-if="baStyle(b) === 'slider'" :before="pair.before" :after="pair.after" :labels="baLabels(b)" />
          <div v-else class="s2-ba__pair">
            <div class="s2-ba__half"><Media :media="pair.before" ratio="4 / 3" :zoom="true" @zoom="zoomTo(pair.before)" /><span class="t-badge s2-ba__tag">{{ baLabels(b).before }}</span></div>
            <div class="s2-ba__half"><Media :media="pair.after" ratio="4 / 3" :zoom="true" @zoom="zoomTo(pair.after)" /><span class="t-badge s2-ba__tag">{{ baLabels(b).after }}</span></div>
          </div>
          <figcaption v-if="pair.caption" class="t-muted">{{ pair.caption }}</figcaption>
        </figure>
      </div>

      <!-- Post-only blocks -->
      <aside v-else-if="b.kind === 'callout'" class="s2-b s2-b--callout mo-reveal">
        <span v-if="b.emoji" class="s2-callout__emoji" aria-hidden="true">{{ b.emoji }}</span>
        <div class="s2-prose" v-html="safeBlockHtml({html: b.html})" />
      </aside>

      <details v-else-if="b.kind === 'toggle'" class="s2-b s2-b--toggle">
        <summary>{{ b.title || 'More' }}</summary>
        <div class="s2-prose" v-html="safeBlockHtml({html: b.html})" />
      </details>

      <a v-else-if="b.kind === 'link-card' && safeLink(b.url)" class="s2-b s2-b--linkcard t-card mo-reveal" :href="back(safeLink(b.url))"
         :target="isExternal(b.url) ? '_blank' : null" :rel="isExternal(b.url) ? 'noopener' : null">
        <span class="s2-linkcard__text">
          <strong>{{ b.title || linkHost(b.url) || b.url }}</strong>
          <span v-if="b.text" class="t-muted">{{ b.text }}</span>
          <span class="t-eyebrow">{{ linkHost(b.url) || 'This site' }}</span>
        </span>
        <Media v-if="b.image" :media="b.image" class="s2-linkcard__img" ratio="4 / 3" />
      </a>

      <a v-else-if="b.kind === 'product-card' && productOf(b)" class="s2-b s2-b--product t-card mo-reveal" :href="back(productOf(b).path)">
        <Media v-if="productOf(b).image" :media="productOf(b).image" class="s2-product__img t-product-photo" ratio="1 / 1" />
        <span class="s2-product__text">
          <strong>{{ productOf(b).name }}</strong>
          <span v-if="productOf(b).price" class="t-price" :class="{'t-price--sale': productOf(b).wasPrice}">£{{ productOf(b).price }}<span v-if="productOf(b).wasPrice" class="t-price__was">£{{ productOf(b).wasPrice }}</span></span>
          <span class="t-link s2-arrow">{{ productOf(b).inShop ? 'See in shop' : 'Find out more' }}</span>
        </span>
      </a>

      <a v-else-if="b.kind === 'file' && b.href" class="s2-b s2-b--file t-card mo-reveal" :href="b.href" download>
        <span aria-hidden="true" class="s2-file__icon">⤓</span>
        <span><strong>{{ b.label || b.attachment?.name || 'Download' }}</strong><span class="t-muted"> · download</span></span>
      </a>

      <aside v-else-if="b.kind === 'signup'" class="s2-b s2-b--signup t-card mo-reveal">
        <strong class="t-h3">{{ b.heading || 'Get new posts by email' }}</strong>
        <p class="t-muted">{{ b.text || 'One email when there’s something new. Unsubscribe any time.' }}</p>
        <a class="t-btn" :href="newsletterLink || '#newsletter'">Sign up</a>
      </aside>
    </template>
    <PhotoLightbox ref="lightbox" :items="lightboxItems" />
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import Media from "../shared/Media.vue";
import PhotoLightbox from "../shared/PhotoLightbox.vue";
import BeforeAfterSlider from "../shared/BeforeAfterSlider.vue";
import {alignOf, contentsOf, isExternal, linkHost, mediaList, mediaOf, normaliseBlocks, plainText, safeBlockHtml, safeLink} from "@/utils/blocks.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({
  blocks: {type: Array, default: () => []},
  content: {type: String, default: ""},   // legacy Words text, used when there are no blocks
  allowPost: {type: Boolean, default: false},
  products: {type: Object, default: () => ({})},  // productId -> {name, path, price, wasPrice, image, inShop}
  newsletterLink: {type: String, default: ""},
});

const list = computed(() => normaliseBlocks({blocks: props.blocks, content: props.content}, {allowPost: props.allowPost}));
const headingIds = computed(() => Object.fromEntries(contentsOf(list.value).map((c) => [c.blockId, c.id])));

// Every photo in the blocks, in order, for the lightbox.
const lightboxItems = computed(() => {
  const all = [];
  for (const b of list.value) {
    if (b.kind === "photo" || b.kind === "photo-text") all.push(b.image);
    if (b.kind === "photos" || b.kind === "gallery") all.push(...(b.images || []));
    if (b.kind === "before-after" && b.style !== "slider") for (const p of b.pairs || []) all.push(p?.before, p?.after);
  }
  return all.map(mediaOf).filter((m) => m.kind === "image");
});
const lightbox = ref(null);
function zoomTo(media) {
  const src = mediaOf(media).src;
  const i = lightboxItems.value.findIndex((m) => m.src === src);
  lightbox.value?.show(i < 0 ? 0 : i);
}

const videoOf = (b) => (b.video && typeof b.video === "object" ? {...b.video, poster: b.video.poster || b.poster || ""} : {link: typeof b.video === "string" ? b.video : "", poster: b.poster || ""});
const linksOf = (b) => (Array.isArray(b.links) ? b.links : []).map((l) => ({label: l?.label || l?.link, href: safeLink(l?.link)})).filter((l) => l.href && l.label);
const productOf = (b) => props.products?.[b.productId] || null;
// before-after {pairs, style: slider|side|stacked, labels} (P1/TP)
const baPairs = (b) => (Array.isArray(b.pairs) ? b.pairs : []).filter((p) => p && mediaOf(p.before).kind && mediaOf(p.after).kind);
const baStyle = (b) => (["slider", "side", "stacked"].includes(b.style) ? b.style : "slider");
const baLabels = (b) => {
  const l = b.labels;
  if (Array.isArray(l)) return {before: l[0] || "Before", after: l[1] || "After"};
  return {before: l?.before || "Before", after: l?.after || "After"};
};
</script>

<style scoped>
.s2-blocks { display: flow-root; }
.s2-b { margin: 0 0 1.25rem; }
.s2-b:last-child { margin-bottom: 0; }
.s2-al-center { text-align: center; }
.s2-al-right { text-align: right; }
.s2-al-justify { text-align: justify; }
.s2-prose :deep(p) { margin: 0 0 .9em; line-height: var(--t-leading); }
.s2-prose :deep(p:last-child) { margin-bottom: 0; }
.s2-prose :deep(ul), .s2-prose :deep(ol) { padding-left: 1.4em; margin: 0 0 .9em; }
.s2-prose :deep(ul) { list-style: disc; } .s2-prose :deep(ol) { list-style: decimal; }
.s2-prose :deep(a) { color: var(--t-accent); text-decoration: underline; text-underline-offset: 2px; }
.s2-prose :deep(h2), .s2-prose :deep(h3) { font-family: var(--t-font-display); margin: 1.2em 0 .5em; }
.s2-prose :deep(.ql-align-center) { text-align: center; } .s2-prose :deep(.ql-align-right) { text-align: right; } .s2-prose :deep(.ql-align-justify) { text-align: justify; }
.s2-b--text[data-lead="true"] { font-size: clamp(1.1rem, 1.7vw, 1.3rem); color: var(--t-muted); }
.s2-b--heading { margin-top: 2rem; scroll-margin-top: 6rem; }
.s2-b--photo { margin-left: auto; margin-right: auto; }
.s2-size-s.s2-b--photo { max-width: 40%; } .s2-size-m.s2-b--photo { max-width: 62%; } .s2-size-l.s2-b--photo { max-width: 82%; }
.s2-size-full.s2-b--photo { max-width: 100%; }
.s2-size-wide.s2-b--photo { width: calc(100% + 8vw); max-width: calc(100% + 8vw); margin-left: -4vw; }
.s2-b--photos { display: grid; gap: var(--t-gap); grid-template-columns: repeat(var(--n, 3), minmax(0, 1fr)); }
.s2-b--photos[data-count="1"] { --n: 1; } .s2-b--photos[data-count="2"] { --n: 2; }
.s2-b--pt { display: flow-root; }
.s2-pt__media { width: 42%; }
.s2-pt-left .s2-pt__media { float: left; margin: .25rem 1.5rem .75rem 0; }
.s2-pt-right .s2-pt__media { float: right; margin: .25rem 0 .75rem 1.5rem; }
.s2-pt-above .s2-pt__media, .s2-pt-below .s2-pt__media { width: 100%; margin: 0 0 1rem; }
.s2-pt-below { display: flex; flex-direction: column-reverse; }
.s2-pt-below .s2-pt__media { margin: 1rem 0 0; }
.s2-size-s .s2-pt__media { width: 30%; } .s2-size-l .s2-pt__media { width: 55%; }
.s2-b--quote { margin: 2rem 0; padding: .25rem 0 .25rem 1.25rem; border-left: 4px solid var(--t-accent); }
.s2-b--quote.s2-al-center { border-left: 0; padding-left: 0; }
.s2-b--quote p { font-size: clamp(1.25rem, 2.4vw, 1.75rem); line-height: 1.3; margin: 0; font-style: italic; font-family: var(--t-font-display); }
.s2-b--quote footer { margin-top: .5rem; font-size: .95rem; }
.s2-b--cols { display: grid; gap: clamp(1rem, 3vw, 2rem); grid-template-columns: repeat(var(--cols), minmax(0, 1fr)); }
.s2-b--ticks { list-style: none; padding: 0; display: grid; gap: .5rem; }
.s2-b--ticks li { display: flex; gap: .6rem; align-items: baseline; }
.s2-tick { color: var(--t-accent); font-weight: 800; }
.s2-b--note { padding: 1rem 1.25rem; background: var(--t-surface-2); }
.s2-b--divider { border: 0; border-top: 1px solid var(--t-line); margin: 2rem 0; }
.s2-b--links ul { list-style: none; padding: 0; display: grid; gap: .4rem; }
.s2-arrow::after { content: " →"; }
.s2-ba { margin: 0 0 1rem; }
.s2-ba__pair { display: grid; grid-template-columns: 1fr 1fr; gap: .5rem; }
.s2-b--ba[data-style="stacked"] .s2-ba__pair { grid-template-columns: 1fr; }
.s2-ba__half { position: relative; }
.s2-ba__tag { position: absolute; left: .6rem; top: .6rem; }
.s2-b--callout { display: flex; gap: .9rem; padding: 1rem 1.2rem; background: var(--t-surface-2); border-radius: var(--t-radius); }
.s2-callout__emoji { font-size: 1.4rem; line-height: 1.2; }
.s2-b--toggle { border-top: 1px solid var(--t-line); border-bottom: 1px solid var(--t-line); padding: .8rem 0; }
.s2-b--toggle summary { cursor: pointer; font-weight: 700; list-style: none; }
.s2-b--toggle summary::after { content: "+"; float: right; color: var(--t-accent); }
.s2-b--toggle[open] summary::after { content: "−"; }
.s2-b--toggle .s2-prose { margin-top: .6rem; }
.s2-b--linkcard, .s2-b--product, .s2-b--file { display: flex; gap: 1rem; align-items: center; text-decoration: none; color: inherit; }
.s2-linkcard__text, .s2-product__text { display: flex; flex-direction: column; gap: .3rem; flex: 1; min-width: 0; }
.s2-linkcard__img { width: 160px; flex: none; }
.s2-product__img { width: 120px; flex: none; }
.s2-file__icon { font-size: 1.5rem; color: var(--t-accent); }
.s2-b--signup { display: flex; flex-direction: column; gap: .5rem; align-items: flex-start; }
@media (max-width: 640px) {
  .s2-pt-left .s2-pt__media, .s2-pt-right .s2-pt__media { float: none; width: 100%; margin: 0 0 1rem; }
  .s2-size-s.s2-b--photo, .s2-size-m.s2-b--photo, .s2-size-l.s2-b--photo { max-width: 100%; }
  .s2-b--photos { --n: 2 !important; }
  .s2-b--cols { grid-template-columns: 1fr; }
  .s2-linkcard__img { width: 96px; }
  .s2-product__img { width: 88px; }
}
</style>
